import { memo } from 'react';

import styled from '@emotion/styled';

import { SerializedStyles } from '@emotion/react';

import { mq } from '../../styles';
import COLOR from '../../constant/colorset';
import Loading from '../Loading/Loading';

type ButtonTheme = 'primary' | 'secondary' | 'tertiary';

export interface ButtonProps {
  className?: string;
  text: string;
  type?: 'button' | 'submit';
  theme?: ButtonTheme;
  radius?: number;
  width?: number;
  height?: number;
  icon?: string;
  styleIcon?: {
    css?: SerializedStyles;
    src: string;
  };
  disabled?: boolean;
  onClick?(): void;
  isLoading?: boolean;
  isSubmitLoading?: boolean;
}

const style: Record<
  ButtonTheme,
  {
    color: string;
    backgroundColor: string;
    border: string;
  }
> = {
  primary: {
    color: COLOR.kurlyWhite,
    backgroundColor: COLOR.kurlyPurple,
    border: '0 none',
  },
  secondary: {
    color: COLOR.kurlyPurple,
    backgroundColor: COLOR.kurlyWhite,
    border: `1px solid ${COLOR.kurlyPurple}`,
  },
  tertiary: {
    color: COLOR.kurlyGray800,
    backgroundColor: COLOR.kurlyWhite,
    border: `1px solid ${COLOR.lightGray}`,
  },
};

const BasicButton = styled.button<Pick<ButtonProps, 'width' | 'height' | 'theme' | 'radius'>>`
  display: block;
  padding: 0 10px;
  text-align: center;
  overflow: hidden;
  ${({ width, height, theme, radius }) =>
    mq({
      width: width ? `${width}px` : '100%',
      height: height ? `${height}px` : ['52px', '56px'],
      borderRadius: Number(radius) > -1 ? `${radius}px` : '3px',
      ':disabled':
        theme === 'primary'
          ? {
              backgroundColor: COLOR.lightGray,
            }
          : {
              borderColor: COLOR.lightGray,
              color: COLOR.lightGray,
            },
      ...style[theme as ButtonTheme],
    })}
`;

const LoadingButton = styled(BasicButton)<{ isSubmitLoading?: boolean }>`
  background: ${({ isSubmitLoading }) => (isSubmitLoading ? COLOR.kurlyPurple : COLOR.lightGray)};
  cursor: not-allowed;
  > div {
    display: inline-block;
    position: relative;
    > div:first-of-type {
      max-width: 100px;
      max-height: 100px;
      width: 70%;
      height: 70%;
    }
  }
`;

const Text = styled.span({
  display: 'inline-block',
  fontSize: '16px',
  fontWeight: 500,
});

const DefaultIcon = styled.img({
  display: 'inline-block',
  width: '20px',
  height: '20px',
  marginRight: '4px',
  verticalAlign: 'middle',
});

function Button({
  className,
  text = '',
  type = 'button',
  theme = 'primary',
  radius,
  width,
  height,
  icon,
  styleIcon,
  disabled = false,
  onClick,
  isLoading = false,
  isSubmitLoading = false,
}: ButtonProps) {
  return isLoading || isSubmitLoading ? (
    <LoadingButton
      type={'button'}
      isSubmitLoading={isSubmitLoading}
      className={className}
      theme={theme}
      width={width}
      height={height}
      radius={radius}
    >
      <Loading theme="white" />
    </LoadingButton>
  ) : (
    <BasicButton
      className={className}
      type={type}
      disabled={disabled}
      onClick={onClick}
      theme={theme}
      width={width}
      height={height}
      radius={radius}
    >
      <Text>
        {icon && <DefaultIcon src={icon} alt="" />}
        {styleIcon && <img src={styleIcon.src} css={styleIcon.css} alt="" />}
        {text}
      </Text>
    </BasicButton>
  );
}

export default memo(Button);
